import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Upload, X, ImagePlus, Loader2, Camera, Clipboard, Trash2
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Client-side resize + JPEG compression to ~100-300KB
const compressImage = (file, maxDim = 1400, quality = 0.75) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const RoomPhotosModal = ({ isOpen, onClose, room, propertyId, token, onUpdated }) => {
  const [photos, setPhotos]       = useState([]);
  const [uploading, setUploading] = useState(false);
  const [queue, setQueue]         = useState([]); // pending previews before upload
  const [dragActive, setDragActive] = useState(false);
  const [deletingIdx, setDeletingIdx] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (room) setPhotos(room.photos || []);
  }, [room]);

  const remainingSlots = 10 - photos.length - queue.length;

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) {
      toast({ title: 'No images found', description: 'Please drop/select image files.', variant: 'destructive' });
      return;
    }
    if (files.length > remainingSlots) {
      toast({ title: `Only ${remainingSlots} slot${remainingSlots !== 1 ? 's' : ''} left`, description: 'Max 10 photos per room', variant: 'destructive' });
      return;
    }
    try {
      const compressed = await Promise.all(files.map(f => compressImage(f)));
      setQueue(q => [...q, ...compressed]);
    } catch (e) {
      toast({ title: 'Could not process images', description: 'Try a different file', variant: 'destructive' });
    }
  }, [remainingSlots]);

  const handleFileInputChange = (e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
    e.target.value = '';
  };

  // Paste from clipboard (ctrl/cmd+V)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) {
        e.preventDefault();
        handleFiles(files);
      }
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
  }, [isOpen, handleFiles]);

  const removeFromQueue = (idx) => setQueue(q => q.filter((_, i) => i !== idx));

  const handleUploadAll = async () => {
    if (queue.length === 0) {
      toast({ title: 'Nothing to upload', description: 'Drop some photos first.' });
      return;
    }
    setUploading(true);
    try {
      const res = await axios.post(
        `${API}/properties/${propertyId}/rooms/${room.id}/photos`,
        { photos: queue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotos(res.data.photos || []);
      setQueue([]);
      toast({ title: `📸 ${res.data.added} photos added`, description: `${res.data.total} total photos on Room ${room.room_number}` });
      onUpdated?.(res.data.photos);
    } catch (e) {
      toast({
        title: 'Upload failed',
        description: e.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    } finally { setUploading(false); }
  };

  const handleDeleteServer = async (idx) => {
    setDeletingIdx(idx);
    try {
      const res = await axios.delete(
        `${API}/properties/${propertyId}/rooms/${room.id}/photos/${idx}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotos(res.data.photos || []);
      onUpdated?.(res.data.photos);
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    } finally { setDeletingIdx(null); }
  };

  const onDragEnter = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragActive(false); };
  const onDragOver  = (e) => { e.preventDefault(); };
  const onDrop      = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0" data-testid="room-photos-modal">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-5">
          <div className="flex items-center gap-2 mb-1">
            <Camera size={20} />
            <DialogTitle className="text-lg font-semibold">
              Room {room?.room_number} — Photos
            </DialogTitle>
          </div>
          <DialogDescription className="text-white/80 text-xs">
            Drop photos here, paste from clipboard, or pick from your device. {photos.length + queue.length}/10 used.
          </DialogDescription>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Dropzone */}
          <div
            data-testid="room-photos-dropzone"
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
              ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/40'}
              ${remainingSlots === 0 ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <Upload size={28} className="mx-auto text-blue-500 mb-2" />
            <p className="text-sm font-semibold text-gray-900">
              {remainingSlots === 0 ? 'Max 10 photos reached' : 'Drop images here, or click to pick'}
            </p>
            <p className="text-[11px] text-gray-500 mt-1">
              <Clipboard size={10} className="inline mr-1" />
              You can also paste (Ctrl+V / ⌘V) directly · JPG/PNG · Auto-compressed to ~200KB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileInputChange}
              className="hidden"
              data-testid="room-photos-file-input"
            />
          </div>

          {/* PENDING QUEUE */}
          {queue.length > 0 && (
            <section data-testid="room-photos-queue">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-700 uppercase">
                  Ready to upload <span className="text-blue-600">({queue.length})</span>
                </p>
                <button
                  onClick={() => setQueue([])}
                  className="text-[11px] text-gray-500 hover:text-red-600"
                  data-testid="room-photos-clear-queue"
                >Clear all</button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {queue.map((src, i) => (
                  <div key={src} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-blue-200">
                    <img src={src} alt={`Pending ${i+1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeFromQueue(i)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center"
                      data-testid={`room-photos-queue-remove-${i}`}
                    ><X size={12} /></button>
                    <span className="absolute bottom-1 left-1 text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-semibold">NEW</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* EXISTING PHOTOS */}
          {photos.length > 0 ? (
            <section data-testid="room-photos-existing">
              <p className="text-xs font-semibold text-gray-700 uppercase mb-2">
                Uploaded <span className="text-gray-500">({photos.length}/10)</span>
              </p>
              <div className="grid grid-cols-4 gap-2">
                {photos.map((src, i) => (
                  <div key={src} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                    <img src={src} alt={`Photo ${i+1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => handleDeleteServer(i)}
                      disabled={deletingIdx === i}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center disabled:opacity-100"
                      data-testid={`room-photos-remove-${i}`}
                    >
                      {deletingIdx === i ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ) : queue.length === 0 && (
            <div className="text-center py-6 text-xs text-gray-500">
              <ImagePlus size={28} className="mx-auto text-gray-300 mb-2" />
              No photos yet. Even 1-2 photos dramatically improve your public listing.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-5 py-3 flex items-center justify-between">
          <Button variant="outline" onClick={onClose} disabled={uploading} data-testid="room-photos-close">
            Done
          </Button>
          {queue.length > 0 && (
            <Button
              data-testid="room-photos-upload-btn"
              onClick={handleUploadAll}
              disabled={uploading}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90"
            >
              {uploading ? (
                <><Loader2 className="animate-spin mr-2" size={14} /> Uploading {queue.length}…</>
              ) : (
                <><Upload size={14} className="mr-1" /> Upload {queue.length} photo{queue.length > 1 ? 's' : ''}</>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoomPhotosModal;
